import { useCallback, useEffect, useRef, useState } from 'react'

export function useGameSound() {
  const [enabled, setEnabled] = useState(true)
  const enabledRef = useRef(true)
  const context = useRef<AudioContext | null>(null)
  const output = useRef<GainNode | null>(null)

  useEffect(
    () => () => {
      void context.current?.close().catch(() => {})
    },
    []
  )

  const getAudio = useCallback(() => {
    if (!enabledRef.current || !window.AudioContext) return null
    const audio = context.current ?? new AudioContext()
    context.current = audio
    if (!output.current) {
      output.current = audio.createGain()
      output.current.connect(audio.destination)
    }
    void audio.resume().catch(() => {})
    return { audio, destination: output.current }
  }, [])

  // Prime audio during the gesture, before an asynchronous reward response arrives.
  const unlock = useCallback(() => {
    try {
      getAudio()
    } catch {
      /* Audio is optional. */
    }
  }, [getAudio])

  const play = useCallback(
    (note = 0) => {
      try {
        const source = getAudio()
        if (!source) return
        const { audio, destination } = source
        const oscillator = audio.createOscillator()
        const gain = audio.createGain()
        oscillator.type = 'sine'
        oscillator.frequency.value = [261.63, 329.63, 392, 523.25][note % 4]
        gain.gain.setValueAtTime(0.06, audio.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.2)
        oscillator.connect(gain)
        gain.connect(destination)
        oscillator.onended = () => {
          oscillator.disconnect()
          gain.disconnect()
        }
        oscillator.start()
        oscillator.stop(audio.currentTime + 0.22)
      } catch {
        /* Devices may block audio. */
      }
    },
    [getAudio]
  )

  // Short, inharmonic metal overtones create a coin clink without downloaded audio.
  const playCoin = useCallback(
    (index = 0) => {
      try {
        const source = getAudio()
        if (!source) return
        const { audio, destination } = source
        const frequency = [1318.51, 1567.98, 1760, 2093][index % 4]
        for (const [ratio, volume, decay] of [
          [1, 0.038, 0.22],
          [2.76, 0.012, 0.12],
          [4.07, 0.005, 0.07],
        ]) {
          const oscillator = audio.createOscillator()
          const gain = audio.createGain()
          oscillator.type = 'sine'
          oscillator.frequency.setValueAtTime(frequency * ratio, audio.currentTime)
          oscillator.frequency.exponentialRampToValueAtTime(
            frequency * ratio * 0.96,
            audio.currentTime + decay
          )
          gain.gain.setValueAtTime(0.0001, audio.currentTime)
          gain.gain.linearRampToValueAtTime(volume, audio.currentTime + 0.004)
          gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + decay)
          oscillator.connect(gain)
          gain.connect(destination)
          oscillator.onended = () => {
            oscillator.disconnect()
            gain.disconnect()
          }
          oscillator.start()
          oscillator.stop(audio.currentTime + decay + 0.02)
        }
      } catch {
        /* A blocked sound must not interrupt a confirmed reward. */
      }
    },
    [getAudio]
  )

  const toggle = useCallback(() => {
    enabledRef.current = !enabledRef.current
    setEnabled(enabledRef.current)
    if (output.current && context.current) {
      output.current.gain.setValueAtTime(
        enabledRef.current ? 1 : 0,
        context.current.currentTime
      )
    }
  }, [])
  return { enabled, toggle, play, playCoin, unlock }
}
